import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarOff, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ClosedDay } from "../../features/closed-days/models/ClosedDay";

// The menu is w-44 (11rem = 176px at the default root font size). Portalling
// to <body> with a measured position needs this in JS terms too — see the
// positioning comment below for why a portal is used at all.
const MENU_WIDTH_PX = 176;
// Two items at px-3 py-2 text-sm plus the p-1 container padding render at
// ~82px; padding this estimate keeps the pre-render flip decision safe
// without needing a measure-then-reposition pass (mirrors the threshold
// SessionParticipants.tsx uses for its tooltip).
const MENU_HEIGHT_ESTIMATE_PX = 100;

interface CalendarDayMenuProps {
  day: Date;
  closed?: ClosedDay;
  /** Rendered inside the trigger — the weekday / date treatment of the host view. */
  children: React.ReactNode;
  onManageDay: (day: Date, closed?: ClosedDay) => void;
  onCreateSession: (day: Date) => void;
  /** Extra classes for the trigger button (per-view spacing). */
  triggerClassName?: string;
  /**
   * Overrides the trigger's accessible name. Month view renders only the bare
   * day-of-month digit as visible content, which repeats across adjacent
   * months in the same grid (e.g. two "1" cells) — pass a full date label so
   * screen-reader users get a unique, unambiguous name.
   */
  ariaLabel?: string;
}

const itemClass =
  "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-grey-10 hover:bg-grey-95 dark:text-grey-90 dark:hover:bg-primary-20";

/**
 * The day cell's header doubles as its action menu: "New session" on that day and
 * closing / reopening it. One trigger per day replaces a permanently visible
 * close-day icon on every cell, and gives the whole header row as the tap target.
 */
const CalendarDayMenu: React.FC<CalendarDayMenuProps> = ({
  day,
  closed,
  children,
  onManageDay,
  onCreateSession,
  triggerClassName = "",
  ariaLabel,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Every day cell sits inside the calendar grid's `overflow-hidden` (it clips
  // the grid's own rounded corners), so an absolutely-positioned menu nested
  // in a bottom-row or trailing-edge cell renders past that boundary and gets
  // silently clipped — invisible and unclickable. Portalling to <body> with a
  // measured `position: fixed` (same approach as SessionParticipants.tsx's
  // tooltip) escapes that ancestor entirely.
  useEffect(() => {
    if (!open || !triggerRef.current) {
      setMenuStyle(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    const isRtl = getComputedStyle(triggerRef.current).direction === "rtl";
    const openUpward = rect.bottom + MENU_HEIGHT_ESTIMATE_PX > window.innerHeight;
    const overflowsEnd = isRtl
      ? rect.left - MENU_WIDTH_PX < 0
      : rect.left + MENU_WIDTH_PX > window.innerWidth;
    // Anchor to the trigger's inline-start by default; flip to inline-end
    // only when the default placement would run off the viewport edge.
    const left = overflowsEnd ? rect.right - MENU_WIDTH_PX : rect.left;
    setMenuStyle({
      position: "fixed",
      top: openUpward ? undefined : rect.bottom + 4,
      bottom: openUpward ? window.innerHeight - rect.top + 4 : undefined,
      left,
      width: MENU_WIDTH_PX,
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!containerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const pick = (action: () => void) => () => {
    setOpen(false);
    action();
  };

  return (
    <div className="relative" ref={containerRef}>
      {/* The visible date is normally the trigger's accessible name; callers
          whose visible content repeats (e.g. the bare day-of-month digit)
          pass ariaLabel for a unique one. aria-haspopup / aria-expanded carry
          that it opens the day's actions. */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={ariaLabel}
        className={`flex w-full items-center gap-2 rounded-lg px-1 py-2 text-start hover:bg-grey-95 dark:hover:bg-primary-20 ${triggerClassName}`}
      >
        {children}
      </button>

      {/* A plain button list, like every other popover here (ContractRowActions
          / PaymentRowActions): role="menu" would promise the full arrow-key
          menu pattern, which two static actions don't need and this widget
          doesn't implement. */}
      {open &&
        menuStyle &&
        createPortal(
          <div
            ref={menuRef}
            data-calendar-day-menu
            style={menuStyle}
            className="z-20 rounded-xl border border-grey-90 bg-white p-1 shadow-lg dark:border-primary-20 dark:bg-primary-10"
          >
            <button type="button" className={itemClass}
              onClick={pick(() => onCreateSession(day))}>
              <Plus size={14} className="shrink-0" />
              <span>{t("sessions.createSession")}</span>
            </button>
            <button type="button" className={itemClass}
              onClick={pick(() => onManageDay(day, closed))}>
              <CalendarOff size={14} className="shrink-0" />
              <span>
                {closed
                  ? t("calendar.closedDay.reopenAction")
                  : t("calendar.closedDay.closeAction")}
              </span>
            </button>
          </div>,
          document.body
        )}
    </div>
  );
};

export default CalendarDayMenu;
